import { useEffect, useRef, useState } from "react";
import { display, parse } from "../dateInput";

/** A native <input type="date"> renders in the browser's own locale, which HTML
 *  gives no way to override — the same page shows yyyy-mm-dd, mm/dd/yyyy or
 *  dd/mm/yyyy depending on the visitor's browser language. This field types and
 *  displays dd-mm-yyyy everywhere, and keeps the native calendar behind a button
 *  so picking a day still works the way people expect. */
export function DateField({ value, onChange, withTime = false, label }: {
  value: string;
  /** Emits the same shape a native input would: YYYY-MM-DD, or YYYY-MM-DDTHH:mm with time. */
  onChange: (value: string) => void;
  withTime?: boolean;
  label: string;
}) {
  const [text, setText] = useState(() => display(value, withTime));
  const native = useRef<HTMLInputElement>(null);
  // Re-sync whenever the owner changes the value (preset ranges, reset buttons).
  useEffect(() => { setText(display(value, withTime)); }, [value, withTime]);

  function commit(raw: string) {
    const parsed = parse(raw, withTime);
    if (parsed) { onChange(parsed); setText(display(parsed, withTime)); }
    else setText(display(value, withTime));
  }
  function openPicker() {
    const element = native.current;
    if (!element) return;
    if (typeof element.showPicker === "function") { try { element.showPicker(); return; } catch { /* not allowed here; fall through */ } }
    element.focus();
    element.click();
  }

  return <span className={withTime ? "date-field with-time" : "date-field"}>
    <input className="date-field-text" aria-label={label} value={text}
      placeholder={withTime ? "dd-mm-yyyy hh:mm" : "dd-mm-yyyy"} inputMode="numeric" autoComplete="off"
      onChange={(event) => setText(event.target.value)}
      onBlur={(event) => commit(event.target.value)}
      onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); commit((event.target as HTMLInputElement).value); } }}/>
    <button type="button" className="date-field-button" aria-label={`Open calendar for ${label}`} onClick={openPicker}>
      <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
        <rect x="3.5" y="5" width="17" height="15" rx="2.5"/><path d="M3.5 9.5h17M8 3.5v3M16 3.5v3"/>
      </svg>
    </button>
    <input ref={native} className="date-field-native" tabIndex={-1} aria-hidden="true"
      type={withTime ? "datetime-local" : "date"} value={value}
      onChange={(event) => onChange(event.target.value)}/>
  </span>;
}
